import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IDataSourceDefinition } from '../../../../../../shared/interfaces/data-source/data-source-definitions/data-source-definition.interface';
import { IDataSourceDefinitionProperty } from '../../../../../../shared/interfaces/data-source/data-source-definitions/data-source-property-definition.interface';
import { IUIDataSourceItem } from '../../../../data-source-items/interfaces/ui-data-source-item';
import { IViewTileDefinition } from '../../../interfaces/view-tile-definition.interface';

interface IDisplayProperty {
  displayIcon?: string;
  property: IDataSourceDefinitionProperty;
  control?: FormControl;
  config: any;
  sourceIndex: number;
}

@Component({
  standalone: false,
  selector: 'suvo-bi-properties-view-tile',
  templateUrl: './properties-view-tile.component.html',
  styleUrls: ['./properties-view-tile.component.scss'],
})
export class PropertiesViewTileComponent implements OnInit {
  @Input() viewTile: IViewTileDefinition;
  @Input() item: IUIDataSourceItem;
  @Input() form: FormGroup;
  @Input() definition: IDataSourceDefinition;
  @Input() editItemMode: boolean;

  displays: IDisplayProperty[];

  ngOnInit() {
    this.loadProperties();
  }

  ngOnChanges() {
    this.loadProperties();
  }

  trackDisplay(index: number, display: IDisplayProperty) {
    return display.property.name;
  }

  private hasValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  private hasDisplayValue(display: IDisplayProperty, value: unknown): boolean {
    if (display.property.displayOptions?.zeroIsNull && value === 0) {
      return false;
    }

    return this.hasValue(value);
  }

  private getSortOrder(display: IDisplayProperty): number | null {
    const order = display.property.displayOptions?.order;

    return typeof order === 'number' && order > 0 ? order : null;
  }

  loadProperties() {
    this.displays = this.viewTile.propertyKeys
      .map(
        (
          propertySetting: { property: string; icon: string },
          sourceIndex: number,
        ): IDisplayProperty | null => {
        const key = propertySetting.property;
        const property = this.definition.properties.find((candidate) => candidate.name === key);

        if (!property) {
          return null;
        }

        const control = this.form?.controls[key] as FormControl | undefined;
        const value = control?.value ?? this.item?.data?.[key];

        if (!this.editItemMode && !this.hasDisplayValue({ property, control, config: propertySetting, sourceIndex }, value)) {
          return null;
        }

        return {
          displayIcon: propertySetting.icon,
          property,
          control,
          config: propertySetting,
          sourceIndex,
        };
        },
      )
      .filter((display: IDisplayProperty | null): display is IDisplayProperty => display !== null)
      .sort((left: IDisplayProperty, right: IDisplayProperty) => {
        const leftOrder = this.getSortOrder(left);
        const rightOrder = this.getSortOrder(right);

        if (leftOrder === null && rightOrder === null) {
          return left.sourceIndex - right.sourceIndex;
        }

        if (leftOrder === null) {
          return 1;
        }

        if (rightOrder === null) {
          return -1;
        }

        if (leftOrder === rightOrder) {
          return left.sourceIndex - right.sourceIndex;
        }

        return leftOrder - rightOrder;
      });
  }
}
